import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useSecureAuth } from '../contexts/SecureAuthContext';
import { toast } from 'sonner';
import { 
  Shield, Lock, Smartphone, Key, History, 
  Eye, EyeOff, Check, X, Loader2, Copy, AlertTriangle
} from 'lucide-react';

const SecuritySettings = () => {
  const { user, toggle2FA, changePassword, getSecurityLogs } = useSecureAuth();
  
  const [loading, setLoading] = useState(false);
  const [securityLogs, setSecurityLogs] = useState([]);
  const [showRecoveryCodes, setShowRecoveryCodes] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  
  // Password change state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  // Password validation
  const passwordChecks = {
    length: newPassword.length >= 8,
    uppercase: /[A-Z]/.test(newPassword),
    number: /[0-9]/.test(newPassword),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword)
  };
  
  const isPasswordValid = Object.values(passwordChecks).every(Boolean);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;

  useEffect(() => {
    loadSecurityLogs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSecurityLogs = async () => {
    const logs = await getSecurityLogs();
    setSecurityLogs(logs);
  };

  const handleToggle2FA = async () => {
    setLoading(true);
    try {
      const newState = !user.two_factor_enabled;
      const result = await toggle2FA(newState);
      
      if (result.success) {
        if (newState && result.recoveryCodes?.length > 0) {
          setRecoveryCodes(result.recoveryCodes);
          setShowRecoveryCodes(true);
          toast.success('2FA enabled! Save your recovery codes.');
        } else {
          toast.success('Two-factor authentication disabled');
        }
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error('Failed to update 2FA settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!isPasswordValid) {
      toast.error('Please meet all password requirements');
      return;
    }
    
    if (!passwordsMatch) {
      toast.error('Passwords do not match');
      return;
    }
    
    setChangingPassword(true);
    try {
      const result = await changePassword(currentPassword, newPassword);
      
      if (result.success) {
        toast.success('Password changed successfully');
        setShowPasswordForm(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        loadSecurityLogs();
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error('Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const copyRecoveryCodes = () => {
    navigator.clipboard.writeText(recoveryCodes.join('\n'));
    toast.success('Recovery codes copied to clipboard');
  };

  const getActionLabel = (action) => {
    const labels = {
      'login_success': 'Successful login',
      'login_failed': 'Failed login attempt',
      '2fa_success': '2FA verification successful',
      '2fa_enabled': '2FA enabled',
      '2fa_disabled': '2FA disabled',
      'password_changed': 'Password changed',
      'password_reset_requested': 'Password reset requested',
      'password_reset_success': 'Password reset completed'
    };
    return labels[action] || action;
  };

  const getActionColor = (action) => {
    if (action.includes('failed')) return '#ef4444';
    if (action.includes('success') || action === 'login_success') return '#16a34a';
    return '#0073ea';
  };

  const PasswordCheck = ({ passed, label }) => (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: '0.5rem',
      fontSize: '0.8rem',
      color: passed ? '#16a34a' : '#9ca3af'
    }}>
      {passed ? <Check size={14} /> : <X size={14} />}
      {label}
    </div>
  );

  return (
    <div style={{ padding: '1.5rem', maxWidth: '800px', margin: '0 auto' }}>
      {/* SecureAuthContext.user is only populated when the caller signed
          in via the /secure-login flow. Regular (AuthContext) sign-in
          leaves it null — which used to crash the page into a blank
          screen. Route unauthenticated visitors back to the dashboard so
          they can at least see their app. */}
      {!user ? (
        <Navigate to="/dashboard" replace />
      ) : (
        <>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <Shield size={24} color="#0073ea" />
        Security Settings
      </h1>
      <p style={{ color: '#6b7280', marginBottom: '2rem' }}>
        Manage your account security and authentication settings
      </p>

      {/* Recovery Codes Modal */}
      {showRecoveryCodes && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '480px',
            width: '100%'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <AlertTriangle size={24} color="#f59e0b" />
              <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>
                Save Your Recovery Codes
              </h2>
            </div>
            
            <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '1.5rem' }}>
              If you lose access to your email, you can use these one-time codes to sign in. 
              Store them in a safe place.
            </p>
            
            <div style={{
              background: '#f9fafb',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1rem',
              fontFamily: 'monospace'
            }}>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(2, 1fr)', 
                gap: '0.5rem' 
              }}>
                {recoveryCodes.map((code) => (
                  <div key={code} style={{ fontSize: '0.9rem', color: '#374151' }}>
                    {code}
                  </div>
                ))}
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={copyRecoveryCodes}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                <Copy size={16} />
                Copy Codes
              </button>
              <button
                onClick={() => setShowRecoveryCodes(false)}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: '#0073ea',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                I've Saved These
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Two-Factor Authentication */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              background: user?.two_factor_enabled ? '#dcfce7' : '#f3f4f6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Smartphone size={24} color={user?.two_factor_enabled ? '#16a34a' : '#9ca3af'} />
            </div>
            <div>
              <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>
                Two-Factor Authentication
              </h3>
              <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: '0.25rem 0 0' }}>
                {user?.two_factor_enabled 
                  ? 'Enabled - verification code sent via email'
                  : 'Add an extra layer of security to your account'
                }
              </p>
            </div>
          </div>
          <button
            onClick={handleToggle2FA}
            disabled={loading}
            style={{
              padding: '0.75rem 1.5rem',
              background: user?.two_factor_enabled ? '#fee2e2' : '#0073ea',
              color: user?.two_factor_enabled ? '#dc2626' : '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: 500,
              cursor: loading ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : null}
            {user?.two_factor_enabled ? 'Disable' : 'Enable'}
          </button>
        </div>
      </div>

      {/* Change Password */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              background: '#f3f4f6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Lock size={24} color="#6b7280" />
            </div>
            <div>
              <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>
                Password
              </h3>
              <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: '0.25rem 0 0' }}>
                Change your account password
              </p>
            </div>
          </div>
          {!showPasswordForm && (
            <button
              onClick={() => setShowPasswordForm(true)}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#f3f4f6',
                color: '#374151',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.9rem',
                fontWeight: 500,
                cursor: 'pointer'
              }}
            >
              Change Password
            </button>
          )}
        </div>

        {showPasswordForm && (
          <form onSubmit={handleChangePassword} style={{ marginTop: '1.5rem', borderTop: '1px solid #e5e7eb', paddingTop: '1.5rem' }}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>
                Current Password
              </label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>
                New Password
              </label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  boxSizing: 'border-box'
                }}
              />
              
              {newPassword.length > 0 && (
                <div style={{ 
                  marginTop: '0.75rem', 
                  padding: '0.75rem', 
                  background: '#f9fafb', 
                  borderRadius: '8px',
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '0.5rem'
                }}>
                  <PasswordCheck passed={passwordChecks.length} label="8+ characters" />
                  <PasswordCheck passed={passwordChecks.uppercase} label="Uppercase letter" />
                  <PasswordCheck passed={passwordChecks.number} label="Number" />
                  <PasswordCheck passed={passwordChecks.special} label="Special character" />
                </div>
              )}
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>
                Confirm New Password
              </label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: `1px solid ${confirmPassword.length > 0 ? (passwordsMatch ? '#16a34a' : '#ef4444') : '#e5e7eb'}`,
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showPassword}
                onChange={(e) => setShowPassword(e.target.checked)}
              />
              <span style={{ fontSize: '0.9rem', color: '#6b7280' }}>Show passwords</span>
            </label>

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                type="button"
                onClick={() => {
                  setShowPasswordForm(false);
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmPassword('');
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={changingPassword || !isPasswordValid || !passwordsMatch}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: isPasswordValid && passwordsMatch ? '#0073ea' : '#e5e7eb',
                  color: isPasswordValid && passwordsMatch ? '#fff' : '#9ca3af',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  cursor: isPasswordValid && passwordsMatch ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                {changingPassword ? <Loader2 size={16} className="animate-spin" /> : null}
                {changingPassword ? 'Saving...' : 'Update Password'}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Security Activity */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
          <History size={20} color="#0073ea" />
          <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>
            Recent Security Activity
          </h3>
        </div>

        {securityLogs.length === 0 ? (
          <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>No recent activity</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {securityLogs.slice(0, 10).map((log) => (
              <div 
                key={log.id || `${log.timestamp || ''}-${log.event_type || ''}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.75rem',
                  background: '#f9fafb',
                  borderRadius: '8px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: getActionColor(log.action)
                  }} />
                  <div>
                    <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: 500 }}>
                      {getActionLabel(log.action)}
                    </p>
                    <p style={{ margin: '0.125rem 0 0', fontSize: '0.75rem', color: '#6b7280' }}>
                      {log.ip_address}
                    </p>
                  </div>
                </div>
                <span style={{ fontSize: '0.8rem', color: '#9ca3af' }}>
                  {new Date(log.timestamp).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
        </>
      )}
    </div>
  );
};

export default SecuritySettings;
